export interface ApiErrorShape {
  ok?: false;
  error?: string;
  /** Human-readable text when `error` is a machine code (registration routes) */
  message?: string;
  field?: string;
  attemptsLeft?: number;
  retryAfterSeconds?: number;
}

export class ApiError extends Error {
  status: number;
  /** Raw `error` value from the response body (a code on registration routes) */
  code?: string;
  field?: string;
  attemptsLeft?: number;
  retryAfterSeconds?: number;

  constructor(
    status: number,
    message: string,
    code?: string,
    field?: string,
    extra?: { attemptsLeft?: number; retryAfterSeconds?: number },
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.field = field;
    this.attemptsLeft = extra?.attemptsLeft;
    this.retryAfterSeconds = extra?.retryAfterSeconds;
    this.name = 'ApiError';
  }
}

import { apiUrl } from './api-base';
import { FEATURE_DISABLED } from './feature-access';

export async function apiFetch<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(apiUrl(path), {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.body && typeof init.body === 'string'
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...init?.headers,
    },
  });

  const contentType = res.headers.get('content-type') || '';
  let data: unknown;

  if (contentType.includes('application/json')) {
    data = await res.json();
  } else {
    data = await res.text();
  }

  if (!res.ok) {
    const body = typeof data === 'object' && data !== null ? (data as ApiErrorShape) : null;
    const code = body && 'error' in body ? String(body.error || res.statusText) : res.statusText;
    if (code === FEATURE_DISABLED) {
      window.dispatchEvent(new Event('smym:feature-access-changed'));
    }
    const message = typeof body?.message === 'string' && body.message ? body.message : code;
    const retryHeader = Number(res.headers.get('Retry-After'));
    const retryAfterSeconds =
      typeof body?.retryAfterSeconds === 'number'
        ? body.retryAfterSeconds
        : Number.isFinite(retryHeader) && retryHeader > 0
          ? retryHeader
          : undefined;
    throw new ApiError(res.status, message, code, body?.field, {
      attemptsLeft: typeof body?.attemptsLeft === 'number' ? body.attemptsLeft : undefined,
      retryAfterSeconds,
    });
  }

  return data as T;
}
